import { z } from 'zod';
import { AudienceSegment } from '../enums';
import { cnpjSchema, moneyCentsSchema, phoneSchema, urlSchema } from './common';

export const createClientSchema = z.object({
  name: z.string().trim().min(2).max(160),
  legalName: z.string().trim().max(200).optional(),
  document: cnpjSchema.optional(),
  slug: z
    .string()
    .trim()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9-]+$/, 'Use apenas minúsculas, números e hífen'),
  website: urlSchema.optional(),
  phone: phoneSchema.optional(),
  whatsapp: phoneSchema.optional(),
  email: z.string().email().optional(),
  isActive: z.boolean().default(true),
  monthlyBudgetCents: moneyCentsSchema.optional(),
  notes: z.string().max(5000).optional(),
});
export type CreateClientInput = z.infer<typeof createClientSchema>;

export const updateClientSchema = createClientSchema.partial();
export type UpdateClientInput = z.infer<typeof updateClientSchema>;

/**
 * Perfil do cliente — o coração da base de conhecimento reutilizada em TODA
 * geração de IA. `forbiddenTerms` e `mandatoryDisclaimers` viram restrições
 * duras no prompt.
 */
export const clientProfileSchema = z.object({
  positioning: z.string().max(4000).optional(),
  toneOfVoice: z.string().max(2000).optional(),
  targetAudience: z.string().max(4000).optional(),
  differentials: z.array(z.string().max(300)).max(30).default([]),
  competitors: z.array(z.string().max(160)).max(30).default([]),
  preferredTerms: z.array(z.string().max(120)).max(60).default([]),
  forbiddenTerms: z.array(z.string().max(120)).max(60).default([]),
  /** Restrições e informações que NÃO podem aparecer nos anúncios. */
  advertisingRestrictions: z.array(z.string().max(500)).max(40).default([]),
  mandatoryDisclaimers: z.array(z.string().max(500)).max(20).default([]),
  supportHours: z.string().max(300).optional(),
  installationSla: z.string().max(300).optional(),
  paymentMethods: z.array(z.string().max(120)).max(20).default([]),
  extraContext: z.string().max(8000).optional(),
});
export type ClientProfileInput = z.infer<typeof clientProfileSchema>;

export const clientPlanSchema = z.object({
  name: z.string().trim().min(2).max(160),
  downloadMbps: z.number().int().min(1).max(100000),
  uploadMbps: z.number().int().min(1).max(100000).optional(),
  priceCents: moneyCentsSchema,
  promoPriceCents: moneyCentsSchema.optional(),
  promoMonths: z.number().int().min(1).max(36).optional(),
  installationFeeCents: moneyCentsSchema.optional(),
  loyaltyMonths: z.number().int().min(0).max(48).default(0),
  hasWifi: z.boolean().default(true),
  wifiDescription: z.string().max(300).optional(),
  includedApps: z.array(z.string().max(120)).max(30).default([]),
  benefits: z.array(z.string().max(300)).max(30).default([]),
  segment: z.nativeEnum(AudienceSegment).default(AudienceSegment.RESIDENCIAL),
  /** IDs de client_service_areas onde o plano é vendido. Vazio = todas. */
  serviceAreaIds: z.array(z.string().uuid()).default([]),
  isActive: z.boolean().default(true),
  highlight: z.boolean().default(false),
  displayOrder: z.number().int().min(0).default(0),
  legalNotes: z.string().max(1000).optional(),
});
export type ClientPlanInput = z.infer<typeof clientPlanSchema>;

export const updateClientPlanSchema = clientPlanSchema.partial();

export const serviceAreaSchema = z.object({
  city: z.string().trim().min(2).max(120),
  state: z.string().trim().length(2).toUpperCase(),
  neighborhoods: z.array(z.string().max(120)).max(200).default([]),
  postalCodes: z.array(z.string().max(12)).max(500).default([]),
  radiusKm: z.number().min(0.5).max(200).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  /** Cobertura confirmada — usada para filtrar segmentação e qualificar leads. */
  hasCoverage: z.boolean().default(true),
  isActive: z.boolean().default(true),
});
export type ServiceAreaInput = z.infer<typeof serviceAreaSchema>;

export const clientFeatureSchema = z.object({
  key: z
    .string()
    .trim()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9_.-]+$/),
  label: z.string().trim().min(2).max(160),
  value: z.string().max(2000).optional(),
  isEnabled: z.boolean().default(true),
  category: z.string().max(80).optional(),
});
export type ClientFeatureInput = z.infer<typeof clientFeatureSchema>;

/** Metas comerciais usadas pelas regras determinísticas de otimização. */
export const clientGoalsSchema = z.object({
  targetCplCents: moneyCentsSchema.optional(),
  targetCostPerInstallCents: moneyCentsSchema.optional(),
  targetLeadsPerMonth: z.number().int().min(0).optional(),
  targetInstallsPerMonth: z.number().int().min(0).optional(),
  averageTicketCents: moneyCentsSchema.optional(),
  averageCustomerLifetimeMonths: z.number().int().min(1).max(240).optional(),
});
export type ClientGoalsInput = z.infer<typeof clientGoalsSchema>;
